import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import {
    purchaseFunds,
    fetchFundSummary,
} from '../../../features/fundManagement/fundManagementSlice'

import './PurchaseFundsForm.css'
import CTextField from '../../../components/UI/CTextField'
import CButton from '../../../components/UI/CButton'
import Icon from '../../../components/UI/Icon'
import CNumericField from '../../../components/UI/CNumericField'

/**
 * Reusable component for purchasing funds
 * Handles all purchase logic internally and only exposes open/close callbacks
 */
const PurchaseFundsForm = ({
    onClose,
    onSuccess,
    onError,
    currency = 'USD',
    loadFeePercentage = 2.5,
    vatPercentage = 0,
}) => {
    const dispatch = useDispatch()
    const { loading } = useSelector((state) => state.fundManagement)

    const [amount, setAmount] = useState('')
    const [validationError, setValidationError] = useState(false)
    const [errorText, setErrorText] = useState('')
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' })

    // Reset form when component mounts
    useEffect(() => {
        setAmount('')
        setValidationError(false)
        setErrorText('')
    }, [])

    const calculateSummary = () => {
        const purchaseAmount = parseFloat(amount) || 0
        // const loadFee = purchaseAmount * (loadFeePercentage / 100)
        const loadFee = 0 // Temporarily set to 0 until we decide on fee structure
        const subtotal = purchaseAmount + loadFee
        const vat = subtotal * (vatPercentage / 100)
        const total = subtotal + vat

        return {
            purchaseAmount,
            loadFee,
            vat,
            total,
        }
    }

    const formatAmount = (value) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency,
        }).format(value)
    }

    const handleAmountChange = (value) => {
        setAmount(value)
        if (validationError) {
            setValidationError(false)
            setErrorText('')
        }
    }

    const handleCancel = () => {
        setAmount('')
        setValidationError(false)
        setErrorText('')
        if (onClose) onClose()
    }

    const handleSubmit = async () => {
        const parsedAmount = parseFloat(amount)

        if (isNaN(parsedAmount) || parsedAmount <= 0) {
            setValidationError(true)
            setErrorText('Please enter a valid amount greater than 0')
            return
        }

        try {
            await dispatch(purchaseFunds(parsedAmount)).unwrap()

            setSnackbar({
                open: true,
                message: `Successfully purchased ${formatAmount(parsedAmount)} in funds`,
                severity: 'success',
            })

            // Refresh wallet info
            dispatch(fetchFundSummary())

            // Reset form
            setAmount('')
            setValidationError(false)
            setErrorText('')

            if (onSuccess) onSuccess(parsedAmount)
            if (onClose) onClose()
        } catch (err) {
            const errorMessage = err?.message || err?.toString() || 'Unknown error'

            setSnackbar({
                open: true,
                message: `Failed to purchase funds: ${errorMessage}`,
                severity: 'error',
            })

            if (onError) onError(err)
        }
    }

    const handleSnackbarClose = () => {
        setSnackbar({ ...snackbar, open: false })
    }

    const summary = calculateSummary()

    return (
        <div className="purchase-funds-form">
            <span className="form-description">
                Enter the amount of funds you wish to purchase. An invoice will be
                generated for your payment.
            </span>

            <CNumericField
                label="Amount"
                returnCurrencyValue
                required
                onChange={(val) => handleAmountChange(val)}
                helperText={validationError ? errorText : `Enter amount in ${currency}`}
                isError={validationError}
                disabled={loading.purchase}
            />

            <span className="header-4">Purchase summary</span>
            <div className="purchase-summary">
                <div className="summary-row-item">
                    <span className="text-4">Purchase amount</span>
                    <span className="text-4">{formatAmount(summary.purchaseAmount)}</span>
                </div>
                {/* <div className="summary-row-item">
                    <span className="text-4">Load fee ({loadFeePercentage}%)</span>
                    <span className="text-4">{formatAmount(summary.loadFee)}</span>
                </div> */}
                <div className="summary-row-item">
                    <span className="text-4">VAT ({vatPercentage}%)</span>
                    <span className="text-4">{formatAmount(summary.vat)}</span>
                </div>
                <div className="divider"></div>
                <div className="summary-row-item">
                    <span className="header-4">Total</span>
                    <span className="header-4">{formatAmount(summary.total)}</span>
                </div>
            </div>

            <div className="purchase-note">
                <div className="icon-wrapper">
                    <Icon name="info-circle" />
                </div>
                <span className="text-4">
                    <b>Please note:</b> Once your request to purchase funds is received, we will confirm with you via email.
                </span>
            </div>

            <div className="form-actions">
                <CButton
                    label="Cancel"
                    onClick={handleCancel}
                    variant="outlined"
                    color="secondary"
                    disabled={loading.purchase}
                />
                <CButton
                    label="Confirm Purchase"
                    onClick={handleSubmit}
                    variant="contained"
                    color="primary"
                    loading={loading.purchase}
                    disabled={loading.purchase || !amount || parseFloat(amount) <= 0}
                />
            </div>
        </div>
    )
}

export default PurchaseFundsForm
